import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Timer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  fetchSlaConfigs,
  createSlaConfigApi,
  updateSlaConfigApi,
  deleteSlaConfigApi,
  fetchOperators,
} from '@/hooks/useTickets';
import type { SlaConfigItem, CreateSlaConfigRequest, UpdateSlaConfigRequest, OperatorListItem } from '@uniticket/shared';

const TICKET_TYPE_VALUES = ['OPEN_ORDER', 'ADDRESS_MANAGEMENT', 'SUPPORT_REQUEST'] as const;
const PRIORITY_VALUES = ['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const;

function formatHours(h: number): string {
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  const rem = h % 24;
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
}

/** Build the auto-generated label from operator + type + priority */
function autoLabel(
  operatorName: string | null,
  ticketType: string,
  priority: string,
  t: (key: string) => string,
): string {
  const op = operatorName ?? t('admin:slaConfig.defaultAll');
  const type = t(`common:ticketType.${ticketType}`);
  const prio = t(`common:priority.${priority}`);
  return `${op} / ${type} / ${prio}`;
}

export function SlaConfigPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [configs, setConfigs] = useState<SlaConfigItem[]>([]);
  const [operators, setOperators] = useState<OperatorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SlaConfigItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [configRes, opRes] = await Promise.all([
        fetchSlaConfigs(),
        fetchOperators(),
      ]);
      setConfigs(configRes.data);
      setOperators(opRes.data);
    } catch (err: any) {
      toast.error(err.message || t('admin:slaConfig.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  function handleAdd() {
    setEditingConfig(null);
    setDialogOpen(true);
  }

  function handleEdit(config: SlaConfigItem) {
    setEditingConfig(config);
    setDialogOpen(true);
  }

  async function handleDelete(id: string) {
    try {
      await deleteSlaConfigApi(id);
      toast.success(t('admin:slaConfig.deleted'));
      setDeleteConfirm(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('admin:slaConfig.deleteError'));
    }
  }

  async function handleSave(data: CreateSlaConfigRequest | UpdateSlaConfigRequest) {
    try {
      if (editingConfig) {
        await updateSlaConfigApi(editingConfig.id, data as UpdateSlaConfigRequest);
        toast.success(t('admin:slaConfig.updated'));
      } else {
        await createSlaConfigApi(data as CreateSlaConfigRequest);
        toast.success(t('admin:slaConfig.created'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('admin:slaConfig.saveError'));
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <Timer className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('admin:slaConfig.title')}</h2>
            <p className="text-sm text-slate-500">{t('admin:slaConfig.count', { count: configs.length })}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="bg-brand-gradient text-white hover:opacity-90">
          <Plus className="w-4 h-4 mr-2" />
          {t('admin:slaConfig.add')}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.rule')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.operator')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.ticketType')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.priority')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.response')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.resolution')}</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.warning')}</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.status')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('admin:slaConfig.cols.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {configs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                    {t('admin:slaConfig.empty')}
                  </td>
                </tr>
              ) : (
                configs.map((config) => {
                  const generated = autoLabel(config.operatorName, config.ticketType, config.priority, t);
                  return (
                    <tr key={config.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700 text-xs">{generated}</span>
                          {config.label && (
                            <span className="text-xs text-brand-rose">{config.label}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-700">{config.operatorName}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {t(`common:ticketType.${config.ticketType}`)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={
                          config.priority === 'URGENT' ? 'red' :
                          config.priority === 'HIGH' ? 'orange' : 'default'
                        }>
                          {t(`common:priority.${config.priority}`)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-600">{formatHours(config.responseTimeHours)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-600">{formatHours(config.resolutionTimeHours)}</td>
                      <td className="px-4 py-3 text-center text-slate-500">{Math.round(config.warningThreshold * 100)}%</td>
                      <td className="px-4 py-3 text-center">
                        {config.isActive ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                            {t('admin:slaConfig.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                            {t('admin:slaConfig.inactive')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEdit(config)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-brand-rose hover:bg-brand-rose/5 transition-colors"
                            title={t('common:actions.edit')}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {deleteConfirm === config.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(config.id)}
                                className="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                              >
                                {t('common:actions.confirm')}
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="p-1 text-slate-400 hover:text-slate-600"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(config.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title={t('common:actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      {dialogOpen && (
        <SlaConfigDialog
          editing={editingConfig}
          operators={operators}
          onSave={handleSave}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}

// ── SLA Config Dialog ────────────────────────────────────

interface SlaConfigDialogProps {
  editing: SlaConfigItem | null;
  operators: OperatorListItem[];
  onSave: (data: CreateSlaConfigRequest | UpdateSlaConfigRequest) => Promise<void>;
  onClose: () => void;
}

const NO_OPERATOR = '__default__';

function SlaConfigDialog({ editing, operators, onSave, onClose }: SlaConfigDialogProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [operatorId, setOperatorId] = useState(editing?.operatorId ?? NO_OPERATOR);
  const [ticketType, setTicketType] = useState(editing?.ticketType || '');
  const [priority, setPriority] = useState(editing?.priority || '');
  const [customLabel, setCustomLabel] = useState(editing?.label || '');
  const [responseTimeHours, setResponseTimeHours] = useState(String(editing?.responseTimeHours || ''));
  const [resolutionTimeHours, setResolutionTimeHours] = useState(String(editing?.resolutionTimeHours || ''));
  const [warningThreshold, setWarningThreshold] = useState(String((editing?.warningThreshold ?? 0.8) * 100));
  const [escalationCooldown, setEscalationCooldown] = useState(
    editing?.escalationEligibleAfterHours != null ? String(editing.escalationEligibleAfterHours) : '',
  );
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  // Compute auto-generated label from current selections
  const selectedOp = operators.find((o) => o.id === operatorId);
  const opName = operatorId === NO_OPERATOR ? null : selectedOp?.name ?? (editing?.operatorName || '');
  const generatedLabel = ticketType && priority ? autoLabel(opName, ticketType, priority, t) : '';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const resolvedOperatorId = operatorId === NO_OPERATOR ? null : operatorId;
      const labelValue = customLabel.trim() || null;

      const cooldownValue = escalationCooldown.trim() === '' ? null : Number(escalationCooldown);
      if (editing) {
        await onSave({
          label: labelValue,
          responseTimeHours: Number(responseTimeHours),
          resolutionTimeHours: Number(resolutionTimeHours),
          warningThreshold: Number(warningThreshold) / 100,
          escalationEligibleAfterHours: cooldownValue,
          isActive,
        } satisfies UpdateSlaConfigRequest);
      } else {
        if (!ticketType || !priority) {
          toast.error(t('common:validation.required'));
          return;
        }
        await onSave({
          operatorId: resolvedOperatorId,
          ticketType,
          priority,
          label: labelValue,
          responseTimeHours: Number(responseTimeHours),
          resolutionTimeHours: Number(resolutionTimeHours),
          warningThreshold: Number(warningThreshold) / 100,
          escalationEligibleAfterHours: cooldownValue,
        } satisfies CreateSlaConfigRequest);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {editing ? t('admin:slaConfig.editTitle') : t('admin:slaConfig.createTitle')}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
          {/* Operator selector */}
          {!editing ? (
            <div className="space-y-1.5">
              <Label>{t('admin:slaConfig.cols.operator')}</Label>
              <Select value={operatorId} onValueChange={setOperatorId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('admin:slaConfig.selectOperator')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_OPERATOR}>{t('admin:slaConfig.defaultAll')}</SelectItem>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">{t('admin:slaConfig.operatorOverride')}</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg px-4 py-2.5">
              <span className="font-semibold text-slate-700">{editing.operatorName}</span>
              <span>&middot;</span>
              <span>{t(`common:ticketType.${editing.ticketType}`)}</span>
              <span>&middot;</span>
              <span>{t(`common:priority.${editing.priority}`)}</span>
            </div>
          )}

          {/* Ticket Type & Priority (only for new) */}
          {!editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('admin:slaConfig.cols.ticketType')}</Label>
                <Select value={ticketType} onValueChange={setTicketType}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin:slaConfig.selectType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_TYPE_VALUES.map((tv) => (
                      <SelectItem key={tv} value={tv}>{t(`common:ticketType.${tv}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('admin:slaConfig.cols.priority')}</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin:slaConfig.selectPriority')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_VALUES.map((pv) => (
                      <SelectItem key={pv} value={pv}>{t(`common:priority.${pv}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Auto-generated label preview */}
          {generatedLabel && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-4 py-2">
              <span className="font-semibold text-slate-600">{t('admin:slaConfig.autoLabel')}</span>{' '}
              <span className="font-mono">{generatedLabel}</span>
            </div>
          )}

          {/* Custom label */}
          <div className="space-y-1.5">
            <Label>{t('admin:slaConfig.customLabel')} <span className="text-slate-400 font-normal">{t('admin:slaConfig.optional')}</span></Label>
            <Input
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder={t('admin:slaConfig.labelPlaceholder')}
              maxLength={150}
            />
          </div>

          {/* Time fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('admin:slaConfig.responseTime')}</Label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={responseTimeHours}
                onChange={(e) => setResponseTimeHours(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('admin:slaConfig.resolutionTime')}</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={resolutionTimeHours}
                onChange={(e) => setResolutionTimeHours(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Warning threshold */}
          <div className="space-y-1.5">
            <Label>{t('admin:slaConfig.warningThreshold')}</Label>
            <Input
              type="number"
              min="50"
              max="99"
              value={warningThreshold}
              onChange={(e) => setWarningThreshold(e.target.value)}
            />
            <p className="text-xs text-slate-400">{t('admin:slaConfig.warningHint')}</p>
          </div>

          {/* Escalation cooldown */}
          <div className="space-y-1.5">
            <Label>{t('admin:slaConfig.escalationCooldown')}</Label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={escalationCooldown}
              onChange={(e) => setEscalationCooldown(e.target.value)}
              placeholder={t('admin:slaConfig.escalationCooldownPlaceholder')}
            />
            <p className="text-xs text-slate-400">{t('admin:slaConfig.escalationCooldownHint')}</p>
          </div>

          {/* Active toggle (edit only) */}
          {editing && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} />
              </button>
              <Label className="cursor-pointer" onClick={() => setIsActive(!isActive)}>
                {isActive ? t('admin:slaConfig.active') : t('admin:slaConfig.inactive')}
              </Label>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
              {saving ? t('admin:slaConfig.saving') : editing ? t('admin:slaConfig.updateRule') : t('admin:slaConfig.createRule')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
